import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Bell, Calendar, Settings, Shield, ShieldCheck, ShieldX } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiService } from '@/services/api';

interface Operator {
  id: string;
  name: string;
  email: string;
  plan: string;
  status: string;
  joined: string;
}

const AdminOperatorsList: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    apiService.getAdminOperators()
      .then((data: any) => {
        let ops: Operator[] = [];
        if (Array.isArray(data)) {
          ops = data;
        } else if (Array.isArray(data?.operators)) {
          ops = data.operators;
        } else if (Array.isArray(data?.results)) {
          ops = data.results;
        }
        setOperators(ops);
        setLoading(false);
      })
      .catch(err => {
        setError("Failed to load operators");
        setLoading(false);
      });
  }, []);

  const handleStatusChange = (id: string, newStatus: string) => {
    const updatedOperators = operators.map(op => 
      op.id === id ? { ...op, status: newStatus } : op
    );
    setOperators(updatedOperators);
    
    toast({
      title: "Operator status updated",
      description: `Operator status changed to ${newStatus}`,
    });
  };

  // Function to get status badge style
  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'active':
        return "bg-green-100 text-green-800";
      case 'pending':
        return "bg-yellow-100 text-yellow-800";
      case 'inactive':
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to get plan badge style
  const getPlanBadge = (plan: string) => {
    switch(plan) {
      case 'premium':
        return "bg-sauna-orange/10 text-sauna-orange";
      case 'basic':
        return "bg-sauna-blue/10 text-sauna-blue";
      default:
        return "bg-gray-100 text-gray-500";
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading operators...</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  return (
    <Card className="border border-gray-200">
      <CardHeader className="pb-2 flex flex-row justify-between items-center">
        <CardTitle className="text-lg">Operator Management</CardTitle>
        <Button size="sm" className="bg-sauna-blue">
          Add New Operator
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Operator</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operators.map((operator) => (
                <TableRow key={operator.id}>
                  <TableCell className="font-medium">{operator.name}</TableCell>
                  <TableCell>{operator.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadge(operator.plan)}`}>
                      {operator.plan.charAt(0).toUpperCase() + operator.plan.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(operator.status)}`}>
                      {operator.status.charAt(0).toUpperCase() + operator.status.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell>{operator.joined}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleStatusChange(operator.id, operator.status === 'active' ? 'inactive' : 'active')}
                      >
                        {operator.status === 'active' ? 
                          <ShieldX className="h-4 w-4 text-red-500" /> : 
                          <ShieldCheck className="h-4 w-4 text-green-500" />
                        }
                      </Button>
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Bell className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            Showing 5 of 32 operators
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" disabled>Previous</Button>
            <Button variant="outline" size="sm">Next</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminOperatorsList;
